import React, { useState, useEffect, useRef, useCallback } from "react";
import { collection, getDocs, addDoc, deleteDoc, doc, updateDoc, serverTimestamp, writeBatch } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { db, storage } from "../../firebase";
import AdminShell from "../../components/AdminShell";
import DocumentTemplateEditor from "../../components/DocumentTemplateEditor";

// Renders **bold** markers and preserves line breaks
function FormattedText({ text }) {
  if (!text) return null;
  return (
    <span>
      {text.split("\n").map((line, li) => (
        <span key={li}>
          {li > 0 && <br />}
          {line.split(/(\*\*[^*]+\*\*)/).map((part, i) =>
            part.startsWith("**") && part.endsWith("**")
              ? <strong key={i}>{part.slice(2, -2)}</strong>
              : <span key={i}>{part}</span>
          )}
        </span>
      ))}
    </span>
  );
}

const blankTemplate = {
  company: "", role: "", longRole: "", duties: "", coeDuties: "",
  endorseAs: "", industry: "", expertise: "",
  manager: "", dtiCert: "", issueCity: "", employerStatus: "current",
  logoUrl: "", letterheadUrl: "", signatureUrl: ""
};
const blank = { country: "", flag: "", title: "", description: "", openings: "", requirements: [], bannerUrl: "", bannerPath: "", documentTemplate: { ...blankTemplate } };


export default function AdminHirings() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);
  const [reqInput, setReqInput] = useState("");
  const [showTplEditor, setShowTplEditor] = useState(false);
  const [bannerUploading, setBannerUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [savingOrder, setSavingOrder] = useState(false);
  const [dragOverId, setDragOverId] = useState(null);
  const dragIdRef = useRef(null);
  const bannerInputRef = useRef();
  const descRef = useRef();

  const applyBold = () => {
    const el = descRef.current;
    if (!el) return;
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const selected = form.description.substring(start, end);
    const before = form.description.substring(0, start);
    const after = form.description.substring(end);
    const newVal = selected
      ? `${before}**${selected}**${after}`
      : `${before}****${after}`;
    setForm({ ...form, description: newVal });
    setTimeout(() => {
      el.focus();
      const pos = selected ? start + selected.length + 4 : start + 2;
      el.setSelectionRange(pos, pos);
    }, 0);
  };

  const uploadBanner = async (file) => {
    if (!file) return;
    setBannerUploading(true);
    try {
      const path = `hiring-banners/${Date.now()}_${file.name}`;
      const snap = await uploadBytes(ref(storage, path), file);
      const url = await getDownloadURL(snap.ref);
      setForm((f) => ({ ...f, bannerUrl: url, bannerPath: path }));
    } catch (e) { setMsg("Banner upload failed: " + e.message); }
    setBannerUploading(false);
  };

  const removeBanner = async () => {
    if (form.bannerPath) { try { await deleteObject(ref(storage, form.bannerPath)); } catch {} }
    setForm((f) => ({ ...f, bannerUrl: "", bannerPath: "" }));
  };

  const load = async () => {
    try {
      const snap = await getDocs(collection(db, "hirings"));
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      // Sort by sortOrder if present, else by createdAt desc
      list.sort((a, b) => {
        if (a.sortOrder !== undefined && b.sortOrder !== undefined) return a.sortOrder - b.sortOrder;
        if (a.sortOrder !== undefined) return -1;
        if (b.sortOrder !== undefined) return 1;
        return (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0);
      });
      setItems(list);
    } catch (e) { console.error(e); }
  };
  useEffect(() => { load(); }, []);

  // Persist new sort order to Firestore in a single batch
  const saveOrder = async (ordered) => {
    setSavingOrder(true);
    try {
      const batch = writeBatch(db);
      ordered.forEach((item, idx) => {
        batch.update(doc(db, "hirings", item.id), { sortOrder: idx });
      });
      await batch.commit();
      setItems(ordered.map((item, idx) => ({ ...item, sortOrder: idx })));
      setMsg("Order saved.");
      setTimeout(() => setMsg(""), 2000);
    } catch (e) { setMsg("Error saving order: " + e.message); }
    setSavingOrder(false);
  };

  const onDragStart = (e, id) => {
    dragIdRef.current = id;
    e.dataTransfer.effectAllowed = "move";
  };
  const onDragOver = (e, id) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverId(id);
  };
  const onDrop = (e, targetId) => {
    e.preventDefault();
    setDragOverId(null);
    const fromId = dragIdRef.current;
    if (!fromId || fromId === targetId) return;
    const reordered = [...items];
    const fromIdx = reordered.findIndex((i) => i.id === fromId);
    const toIdx = reordered.findIndex((i) => i.id === targetId);
    const [moved] = reordered.splice(fromIdx, 1);
    reordered.splice(toIdx, 0, moved);
    saveOrder(reordered);
  };
  const onDragEnd = () => { dragIdRef.current = null; setDragOverId(null); };

  const startEdit = (item) => {
    setEditing(item.id);
    setForm({ ...blank, ...item, documentTemplate: { ...blankTemplate, ...(item.documentTemplate || {}) } });
  };
  const startNew = () => { setEditing("new"); setForm(blank); };
  const cancel = () => { setEditing(null); setForm(blank); setReqInput(""); };

  const addReq = () => {
    if (!reqInput.trim()) return;
    setForm({ ...form, requirements: [...(form.requirements || []), reqInput.trim()] });
    setReqInput("");
  };
  const removeReq = (i) => setForm({ ...form, requirements: form.requirements.filter((_, idx) => idx !== i) });

  const save = async () => {
    setBusy(true); setMsg("");
    try {
      if (editing === "new") {
        await addDoc(collection(db, "hirings"), { ...form, openings: parseInt(form.openings) || 0, sortOrder: items.length, createdAt: serverTimestamp() });
      } else {
        await updateDoc(doc(db, "hirings", editing), { ...form, openings: parseInt(form.openings) || 0, updatedAt: serverTimestamp() });
      }
      await load();
      cancel();
      setMsg("Saved.");
    } catch (e) { setMsg("Error: " + e.message); }
    setBusy(false);
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this hiring?")) return;
    await deleteDoc(doc(db, "hirings", id));
    await load();
  };

  return (
    <>
    {showTplEditor && (
      <DocumentTemplateEditor
        template={form.documentTemplate || blankTemplate}
        onSave={(tpl) => { setForm({ ...form, documentTemplate: tpl }); setShowTplEditor(false); }}
        onCancel={() => setShowTplEditor(false)}
      />
    )}
    <AdminShell title="Available Hirings">
      {msg && <div className="alert alert-info">{msg}</div>}

      <div className="flex-between mb-16">
        <p className="text-muted">Manage jobs displayed on the homepage.</p>
        {!editing && <button className="btn btn-primary" onClick={startNew}><i className="ti ti-plus"></i> Add New Hiring</button>}
      </div>

      {editing && (
        <div className="card mb-24">
          <h3 style={{ fontSize: 18, marginBottom: 16 }}>{editing === "new" ? "Add New Hiring" : "Edit Hiring"}</h3>
          <div className="form-row cols-2">
            <div className="form-group">
              <label className="form-label">Country *</label>
              <input className="form-control" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} placeholder="e.g., Japan" />
            </div>
            <div className="form-group">
              <label className="form-label">Flag (emoji)</label>
              <input className="form-control" value={form.flag} onChange={(e) => setForm({ ...form, flag: e.target.value })} placeholder="ðŸ‡¯ðŸ‡µ" />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Title *</label>
            <input className="form-control" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Skilled Workers" />
          </div>
          <div className="form-group">
            <label className="form-label">Description</label>
            <div style={{ border: "1px solid var(--ink-200)", borderRadius: "var(--radius-md)", overflow: "hidden" }}>
              {/* Toolbar */}
              <div style={{ display: "flex", gap: 4, padding: "6px 8px", background: "var(--cream-warm)", borderBottom: "1px solid var(--ink-200)" }}>
                <button type="button" title="Bold - wraps selected text with **text**"
                  onClick={applyBold}
                  style={{ padding: "3px 10px", borderRadius: 4, border: "1px solid var(--ink-200)", background: "white", fontWeight: 700, fontSize: 14, cursor: "pointer", lineHeight: 1 }}>
                  B
                </button>
                <span style={{ fontSize: 11, color: "var(--ink-400)", alignSelf: "center", marginLeft: 6 }}>
                  Select text then click <strong>B</strong> for bold. Line breaks are preserved.
                </span>
              </div>
              <textarea
                ref={descRef}
                className="form-control"
                style={{ border: "none", borderRadius: 0, resize: "vertical", minHeight: 100 }}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={4}
                placeholder="Write job description here. Select text and click B for bold."
              />
            </div>
            {/* Live preview */}
            {form.description && (
              <div style={{ marginTop: 8, padding: "10px 12px", background: "var(--cream-warm)", borderRadius: 6, fontSize: 13 }}>
                <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--ink-400)", marginBottom: 4 }}>Preview</div>
                <FormattedText text={form.description} />
              </div>
            )}
          </div>

          <div className="form-group">
            <label className="form-label">Job Card Banner Image <span style={{ fontWeight: 400, color: "var(--ink-400)" }}>(optional - shown on homepage job card)</span></label>
            {form.bannerUrl ? (
              <div style={{ position: "relative", borderRadius: 8, overflow: "hidden", marginBottom: 8 }}>
                <img src={form.bannerUrl} alt="Banner preview" style={{ width: "100%", height: 140, objectFit: "cover", display: "block" }} />
                <div style={{ position: "absolute", top: 8, right: 8, display: "flex", gap: 6 }}>
                  <button type="button" className="btn btn-outline" style={{ fontSize: 12, padding: "4px 10px", background: "rgba(255,255,255,0.9)" }}
                    onClick={() => bannerInputRef.current.click()} disabled={bannerUploading}>
                    <i className="ti ti-replace"></i> Replace
                  </button>
                  <button type="button" className="btn btn-danger" style={{ fontSize: 12, padding: "4px 10px" }}
                    onClick={removeBanner}>
                    <i className="ti ti-trash"></i>
                  </button>
                </div>
              </div>
            ) : (
              <div onClick={() => bannerInputRef.current.click()} style={{
                border: "2px dashed var(--ink-200)", borderRadius: 8, padding: "28px 20px",
                textAlign: "center", cursor: "pointer", color: "var(--ink-400)",
                background: "var(--cream-warm)", fontSize: 14
              }}>
                {bannerUploading
                  ? <><span className="spinner" style={{ borderTopColor: "var(--gold-500)", width: 18, height: 18 }}></span> Uploading...</>
                  : <><i className="ti ti-photo-up" style={{ fontSize: 28, display: "block", marginBottom: 6 }}></i>Click to upload banner image (JPG, PNG - recommended 800x300px)</>
                }
              </div>
            )}
            <input ref={bannerInputRef} type="file" accept="image/*" style={{ display: "none" }}
              onChange={(e) => e.target.files[0] && uploadBanner(e.target.files[0])} />
          </div>
          <div className="form-group">
            <label className="form-label">Openings</label>
            <input type="number" className="form-control" value={form.openings} onChange={(e) => setForm({ ...form, openings: e.target.value })} />
          </div>
          <div className="form-group">
            <label className="form-label">Requirements</label>
            <div style={{ display: "flex", gap: 8 }}>
              <input className="form-control" value={reqInput} onChange={(e) => setReqInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addReq())} placeholder="Add a requirement..." />
              <button className="btn btn-outline" type="button" onClick={addReq}><i className="ti ti-plus"></i></button>
            </div>
            <ol style={{ paddingLeft: 20, marginTop: 12 }}>
              {(form.requirements || []).map((r, i) => (
                <li key={i} style={{ marginBottom: 6 }}>
                  {r} <i className="ti ti-x" style={{ color: "var(--danger)", cursor: "pointer", marginLeft: 8 }} onClick={() => removeReq(i)}></i>
                </li>
              ))}
            </ol>
          </div>
          <hr style={{ margin: "20px 0", border: "none", borderTop: "1px solid var(--ink-200)" }} />
          <div className="flex-between mb-8">
            <div>
              <h4 style={{ fontSize: 15, margin: "0 0 4px" }}>
                <i className="ti ti-file-certificate"></i> Document Template
              </h4>
              <p className="text-muted" style={{ fontSize: 13, margin: 0 }}>
                {form.documentTemplate?.company
                  ? <><i className="ti ti-check" style={{ color: "var(--success)" }}></i> <strong>{form.documentTemplate.company}</strong> . {form.documentTemplate.manager}</>
                  : "No template set - COE and Endorsement Letter will not be available for this job."}
              </p>
            </div>
            <button type="button" className="btn btn-outline" onClick={() => setShowTplEditor(true)}>
              <i className="ti ti-layout-sidebar-right-expand"></i>
              {form.documentTemplate?.company ? " Edit Template" : " Configure Template"}
            </button>
          </div>

          <div style={{ display: "flex", gap: 8 }}>
            <button className="btn btn-primary" onClick={save} disabled={busy}><i className="ti ti-check"></i> Save</button>
            <button className="btn btn-outline" onClick={cancel}>Cancel</button>
          </div>
        </div>
      )}

      <div className="card">
        <div className="flex-between mb-12">
          <p className="text-muted" style={{ fontSize: 13 }}>
            <i className="ti ti-arrows-sort"></i> Drag <i className="ti ti-grip-vertical"></i> to reorder - homepage shows jobs in this sequence.
          </p>
          {savingOrder && <span style={{ fontSize: 12, color: "var(--gold-600)" }}><span className="spinner" style={{ width: 12, height: 12, borderWidth: 2, borderTopColor: "var(--gold-500)", display: "inline-block", marginRight: 6 }}></span>Saving...</span>}
        </div>
        {items.length === 0 ? (
          <div className="empty-state"><i className="ti ti-briefcase"></i><div>No hirings yet</div></div>
        ) : (
          <div className="table-wrap"><table className="table">
            <thead><tr><th style={{ width: 32 }}></th><th style={{ width: 32 }}>#</th><th>Banner</th><th>Country</th><th>Title</th><th>Openings</th><th>Docs</th><th></th></tr></thead>
            <tbody>
              {items.map((h, idx) => (
                <tr key={h.id}
                  draggable
                  onDragStart={(e) => onDragStart(e, h.id)}
                  onDragOver={(e) => onDragOver(e, h.id)}
                  onDrop={(e) => onDrop(e, h.id)}
                  onDragEnd={onDragEnd}
                  style={{
                    cursor: "grab",
                    background: dragOverId === h.id ? "var(--gold-50)" : undefined,
                    borderTop: dragOverId === h.id ? "2px solid var(--gold-500)" : undefined,
                    transition: "background 0.15s"
                  }}
                >
                  <td style={{ width: 32, textAlign: "center", cursor: "grab", color: "var(--ink-300)", fontSize: 18 }}>
                    <i className="ti ti-grip-vertical"></i>
                  </td>
                  <td style={{ width: 32, fontWeight: 700, color: "var(--gold-600)", fontFamily: "var(--font-mono)", fontSize: 13 }}>{idx + 1}</td>
                  <td style={{ width: 80 }}>
                    {h.bannerUrl
                      ? <img src={h.bannerUrl} alt="banner" style={{ width: 72, height: 40, objectFit: "cover", borderRadius: 4 }} />
                      : <span className="text-muted" style={{ fontSize: 12 }}>None</span>
                    }
                  </td>
                  <td>{h.flag} {h.country}</td>
                  <td><strong>{h.title}</strong><div className="text-muted" style={{ fontSize: 12 }}>{h.country}</div></td>
                  <td>{h.openings}</td>
                  <td>
                    {h.documentTemplate?.company
                      ? <span className="badge badge-accepted" title={h.documentTemplate.company}><i className="ti ti-file-certificate"></i> Set</span>
                      : <span className="text-muted" style={{ fontSize: 12 }}>-</span>}
                  </td>
                  <td style={{ whiteSpace: "nowrap" }}>
                    <button className="btn btn-outline" onClick={() => startEdit(h)}><i className="ti ti-edit"></i></button>
                    <button className="btn btn-danger" onClick={() => remove(h.id)} style={{ marginLeft: 4 }}><i className="ti ti-trash"></i></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table></div>
        )}
      </div>
    </AdminShell>
    </>
  );
}

